import { getManagedLabelKind } from "../labelUtils";
import { useSelectionStore } from "../../stores/selectionStore";
import { safeTraverse } from "./sceneObjectLookups";

export function isTendonConstraintMarkerKey(key) {
  return key != null && String(key).startsWith("tendon:");
}

export function markerLayerVisibleForKey(key, constraintVisibilityUi) {
  if (!constraintVisibilityUi) return true;
  return isTendonConstraintMarkerKey(key)
    ? constraintVisibilityUi.tendonsVisible !== false
    : constraintVisibilityUi.constraintsVisible !== false;
}

export function markerLabelsVisibleForKey(key, constraintVisibilityUi) {
  if (!constraintVisibilityUi) return true;
  return isTendonConstraintMarkerKey(key)
    ? constraintVisibilityUi.tendonLabelsOff !== true
    : constraintVisibilityUi.labelsOff !== true;
}

/** CSS2D marker name labels reset to visible when rebuilt; main scene visibility runs before marker rebuild. */
export function syncConstraintMarkerCss2dLabelVisibility(group, snapshot) {
  if (!group) return;
  const {
    showConstraintMarkers,
    constraintVisibilityUi,
    selectedEquality,
    selectedTendon,
    focusModeActive,
    focusBodyUuid,
    focusMeshUuid,
    focusCollisionGeomUuid,
    selectionFocusBodiesMulti,
  } = snapshot;

  const sameId = (a, b) => a != null && b != null && String(a) === String(b);

  const markerKeyMatchesFocus = (markerKey, eqUuid, tendonUuid) => {
    if (markerKey == null) return false;
    const keyStr = String(markerKey);
    if (keyStr.startsWith("tendon:")) {
      return typeof tendonUuid === "string" && sameId(keyStr.slice("tendon:".length), tendonUuid);
    }
    return typeof eqUuid === "string" && sameId(keyStr, eqUuid);
  };

  const isFocusOnSelectedConstraintOrTendon =
    !!constraintVisibilityUi?.focus &&
    ((typeof selectedEquality === "string" && !!selectedEquality) ||
      (typeof selectedTendon === "string" && !!selectedTendon));

  const getAncestorUserDataValue = (node, key) => {
    let p = node;
    while (p) {
      if (p.userData && p.userData[key] != null) return p.userData[key];
      p = p.parent;
    }
    return null;
  };
  const getManagedLabelOwnerBodyUuid = (node) =>
    node?.userData?.bodyUuid ?? getAncestorUserDataValue(node, "bodyUuid");
  const isHiddenBySelectionFocus = (node) => {
    if (!focusModeActive) return false;
    if (focusMeshUuid || focusCollisionGeomUuid) return true;
    const ownerBodyUuid = getManagedLabelOwnerBodyUuid(node);
    if (selectionFocusBodiesMulti && typeof selectionFocusBodiesMulti.has === "function") {
      if (!ownerBodyUuid) return true;
      return !selectionFocusBodiesMulti.has(String(ownerBodyUuid));
    }
    if (!focusBodyUuid) return true;
    return !sameId(ownerBodyUuid, focusBodyUuid);
  };

  safeTraverse(group, (obj) => {
    if (getManagedLabelKind(obj) !== "constraint") return;
    if (isHiddenBySelectionFocus(obj)) {
      obj.visible = false;
      return;
    }
    const key = obj.userData?.constraintMarkerKey;
    let visible = false;
    if (
      showConstraintMarkers &&
      markerLayerVisibleForKey(key, constraintVisibilityUi) &&
      markerLabelsVisibleForKey(key, constraintVisibilityUi)
    ) {
      if (isFocusOnSelectedConstraintOrTendon) {
        visible = markerKeyMatchesFocus(key, selectedEquality, selectedTendon);
      } else {
        visible = true;
      }
    }
    obj.visible = visible;
  });
}

/** Equality/tendon marker pick: selects entity and raises the inspector (labels are DOM overlays). */
export function applyConstraintMarkerKeySelection(key) {
  if (key == null || key === "") return;
  const sel = useSelectionStore.getState();
  sel.setLastSelectionSource("scene");
  const keyStr = String(key);
  if (keyStr.startsWith("tendon:")) {
    sel.selectTendon(keyStr.slice("tendon:".length));
  } else {
    sel.selectEquality(keyStr);
  }
  window.dispatchEvent(new CustomEvent("open-properties-panel"));
}
